import { useState, useEffect, Dispatch, SetStateAction, useRef } from "react"
import { JSONStringify, JSONParse } from "../utils/json"

interface StoredValue<T> {
  value: T
  timestamp: number
}

function loadFromStorage<T>(key: string, initialValue: T, retentionMs: number): T {
  try {
    const stored = localStorage.getItem(key)
    if (!stored) {
      return initialValue
    }

    const parsed = JSONParse(stored)
    if (parsed && typeof parsed === "object" && "value" in parsed && "timestamp" in parsed) {
      const { value, timestamp } = parsed as StoredValue<T>
      if (Date.now() - timestamp < retentionMs) {
        return value
      } else {
        localStorage.removeItem(key)
      }
    }

    return initialValue
  } catch (error) {
    console.error(`Failed to load from localStorage for key "${key}":`, error)
    return initialValue
  }
}

function saveToStorage<T>(key: string, value: T): void {
  try {
    const storedValue: StoredValue<T> = {
      value,
      timestamp: Date.now()
    }
    const serialized = JSONStringify(storedValue)

    localStorage.setItem(key, serialized)
  } catch (error) {
    console.error(`Failed to save to localStorage for key "${key}":`, error)
  }
}


export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  retentionDays = 30
): [T, Dispatch<SetStateAction<T>>] {
  const retentionMs = retentionDays * 24 * 60 * 60 * 1000
  const initialValueRef = useRef(initialValue)

  const [state, setState] = useState<T>(() => {
    return loadFromStorage(key, initialValue, retentionMs)
  })

  useEffect(() => {
    setState(loadFromStorage(key, initialValueRef.current, retentionMs))
  }, [key, retentionMs])

  useEffect(() => {
    saveToStorage(key, state)
  }, [key, state])

  return [state, setState]
}
